import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { HiOutlineWrenchScrewdriver } from 'react-icons/hi2';
import { Button, Badge } from 'reactstrap';
import { TaskOccurrenceCard } from '../../taskTemplates/components/TaskOccurrenceCard';
import type { TaskOccurrence } from '../../../core/api/rtk/taskTemplatesApi';
import { getOccurrenceDueMoment, isOccurrenceOverdue } from '../../taskTemplates/helpers';
import SectionSearch from './SectionSearch';
import { filterTaskRows } from '../taskSearch';

interface TaskSource {
  id: number;
  name: string;
  kind: 'group' | 'item';
}

interface MappedPmItem {
  id: string;
  name: string;
  raw?: TaskOccurrence | null;
  source?: TaskSource | null;
}

type SourceSelectHandler = (
  source: { id: number; kind: 'group' | 'item' } | null | undefined
) => void;

interface PreventiveMaintenanceSectionProps {
  items: MappedPmItem[];
  onAdd?: () => void;
  onOpenOccurrence?: (occurrence: TaskOccurrence) => void;
  onArchive?: (item: MappedPmItem) => void;
  showSource?: boolean;
  hideSourceName?: string;
  onSelectSource?: SourceSelectHandler;
}

const renderAdditionalLines = (
  occurrence: TaskOccurrence,
  source: TaskSource | null | undefined,
  hideSourceName: string | undefined,
  onSelectSource: SourceSelectHandler | undefined
): React.ReactNode => {
  const due = getOccurrenceDueMoment(occurrence);
  const overdue = isOccurrenceOverdue(occurrence);
  const dueLabel = due ? due.format('MM/DD/YYYY') : '';
  const interval = occurrence.task?.interval || '';
  const showSource = !!(source?.name && (!hideSourceName || source.name !== hideSourceName));
  if (!dueLabel && !interval && !showSource) return null;
  const clickable = showSource && typeof onSelectSource === 'function';
  const kindLabel = source?.kind === 'group' ? 'Group' : 'Sub-Asset';
  const handleActivate = (): void => {
    if (!clickable || !source) return;
    onSelectSource?.({ id: source.id, kind: source.kind });
  };
  return (
    <div
      className={`maintenance-item-time maintenance-item-time--stacked${overdue ? ' overdue' : ''}`}
    >
      {dueLabel && <span className="maintenance-item-time-label">{dueLabel}</span>}
      {interval && (
        <div className="maintenance-item-location-row">
          <span className="maintenance-item-location">{interval}</span>
        </div>
      )}
      {showSource && source && (
        <span
          className={`asset-section__source-chip asset-section__source-chip--${source.kind}${
            clickable ? ' asset-section__source-chip--clickable' : ''
          }`}
          title={`${kindLabel}: ${source.name}`}
          role={clickable ? 'button' : undefined}
          tabIndex={clickable ? 0 : undefined}
          onClick={
            clickable
              ? (event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  handleActivate();
                }
              : undefined
          }
          onKeyDown={
            clickable
              ? (event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    event.stopPropagation();
                    handleActivate();
                  }
                }
              : undefined
          }
        >
          <span className="asset-section__source-chip-kind">{kindLabel}</span>
          <span className="asset-section__source-chip-name">{source.name}</span>
        </span>
      )}
    </div>
  );
};

const PreventiveMaintenanceSection: React.FC<PreventiveMaintenanceSectionProps> = ({
  items,
  onAdd,
  onOpenOccurrence,
  onArchive,
  showSource,
  hideSourceName,
  onSelectSource,
}) => {
  const intl = useIntl();
  const [searchTerm, setSearchTerm] = useState('');

  const linked = useMemo(() => items.filter((i) => !!i.raw), [items]);
  const rows = useMemo(() => filterTaskRows(linked, searchTerm), [linked, searchTerm]);

  return (
    <div className="asset-section asset-section--pm">
      <div className="asset-section__header">
        <div className="asset-section__header-left">
          <HiOutlineWrenchScrewdriver
            className="asset-section__header-icon"
            size={16}
            color="#6c757d"
          />
          <h3 className="asset-section__title">
            {intl.formatMessage({ id: 'assets.sections.preventiveMaintenance' })}
          </h3>
          <Badge color="warning" pill>
            {rows.length}
          </Badge>
        </div>
        <Button outline color="primary" size="sm" onClick={onAdd}>
          <i className="uil uil-plus mr-1" />
          {intl.formatMessage({ id: 'assets.actions.add' })}
        </Button>
      </div>

      <div className="asset-section__content">
        {linked.length > 0 && (
          <SectionSearch
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder={intl.formatMessage({ id: 'assets.sections.searchTasks' })}
            clearLabel={intl.formatMessage({ id: 'assets.sections.searchClear' })}
          />
        )}
        {linked.length > 0 && rows.length === 0 && (
          <div className="asset-section__no-results">
            {intl.formatMessage({ id: 'assets.sections.searchEmpty' }, { term: searchTerm })}
          </div>
        )}
        <div className="tasks-daily-table asset-section__task-list">
          {rows.map((item) => {
            const occ = item.raw as TaskOccurrence;
            return (
              <div key={item.id} className="maintenance-item asset-section__task-row">
                <TaskOccurrenceCard
                  occurrence={occ}
                  onOpen={(o) => onOpenOccurrence?.(o)}
                  className="tabular maintenance-task-item"
                  showPriorityTag
                  additionalLines={renderAdditionalLines(
                    occ,
                    showSource ? item.source : null,
                    hideSourceName,
                    onSelectSource
                  )}
                />
                {onArchive && (
                  <button
                    type="button"
                    className="asset-section__row-archive"
                    title={intl.formatMessage({ id: 'archive', defaultMessage: 'Archive' })}
                    onClick={() => onArchive(item)}
                  >
                    <i className="uil uil-archive" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PreventiveMaintenanceSection;
